import { Button, Cell, Grid, TextField, Row, H5, Paragraph, Separator, Expander, ExpanderContent } from '@fs/zion-ui'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../hooks/useNotifications'
import { putNameConclusion } from '../../../services/personService'
import { PersonContext } from '../PersonContext'
import PSAttribution from './PSAttribution'
import PSDivider from './PSDivider'

export default function NameSummary({ editable }) {
  const personContext = useContext(PersonContext)
  const name = personContext.currentPerson.nameConclusion
  const { t } = useTranslation()

  return (
    <>
      <div>
        <H5 supplementary>{t('name', 'Name')}</H5>
      </div>
      {name?.value
        ? name.value.nameForms.map((nameForm) => (
            <div key={nameForm.fullText}>
              <NameFormSummary editable={editable} nameForm={nameForm} />
              <PSDivider />
            </div>
          ))
        : null}
    </>
  )
}

function NameFormSummary({ nameForm, editable }) {
  const { currentPerson, fetchCurrentPerson } = useContext(PersonContext)
  const name = currentPerson.nameConclusion
  const { t } = useTranslation()

  const getNamePart = useCallback(
    (partType) => nameForm.parts.filter((part) => part.type.endsWith(partType)),
    [nameForm.parts]
  )
  const getNamePartValue = useCallback(
    (partType) => {
      const namePart = getNamePart(partType)
      return namePart && namePart.length > 0 ? namePart[0].value : ''
    },
    [getNamePart]
  )

  const [fullName, setFullName] = useState(nameForm.fullText)
  const [prefix, setPrefix] = useState(getNamePartValue('Prefix'))
  const [given, setGiven] = useState(getNamePartValue('Given'))
  const [surname, setSurname] = useState(getNamePartValue('Surname'))
  const [suffix, setSuffix] = useState(getNamePartValue('Suffix'))
  const [changed, setChanged] = useState(false)
  const [changeMessage, setChangeMessage] = useState(name?.attribution?.changeMessage)
  const { error } = useNotifications()
  useEffect(() => {
    setFullName(`${prefix} ${given} ${surname} ${suffix}`)
    const didChange =
      prefix !== getNamePartValue('Prefix') ||
      given !== getNamePartValue('Given') ||
      surname !== getNamePartValue('Surname') ||
      suffix !== getNamePartValue('Suffix') ||
      changeMessage !== name?.attribution?.changeMessage
    if (changed !== didChange) {
      setChanged(didChange)
    }
  }, [prefix, given, surname, suffix, getNamePartValue, changed, changeMessage, name?.attribution?.changeMessage])

  let lang = nameForm.lang
  if (nameForm.langtag) {
    lang += ` / ${nameForm.langtag}`
  }

  const getNewNamePart = (type, value) => ({
    type: `http://gedcomx.org/${type}`,
    value,
  })
  const getNewNameParts = () => {
    const newNameParts = []
    newNameParts.push(getNewNamePart('Prefix', prefix))
    newNameParts.push(getNewNamePart('Given', given))
    newNameParts.push(getNewNamePart('Surname', surname))
    newNameParts.push(getNewNamePart('Suffix', suffix))
    return newNameParts.filter((newNamePart) => newNamePart.value && newNamePart.value !== '')
  }
  const equals = (obj1, obj2) => JSON.stringify(obj1) === JSON.stringify(obj2)

  const saveName = () => {
    const nameConclusion = {
      conclusionType: name.conclusionType,
      conclusionId: name.conclusionId,
      preferred: name.preferred,
      value: {
        type: name.value.type,
        nameForms: JSON.parse(JSON.stringify(name.value.nameForms)),
      },
      attribution: {
        changeMessage,
      },
    }
    nameConclusion.value.nameForms.filter((nf) => equals(nf, nameForm))[0].parts = getNewNameParts()
    putNameConclusion(currentPerson.id, nameConclusion)
      .then(() => {
        fetchCurrentPerson()
      })
      .catch((err) => {
        error(
          t('person.summary.errors.savingNameConclusion', {
            defaultValue: `Error saving Name Conclusion.:${err.response.data.notifications[0].msg}`,
            message: err.response.data.notifications[0].msg,
          }),
          err
        )
      })
  }
  const cancelChanges = () => {
    setPrefix(getNamePartValue('Prefix'))
    setGiven(getNamePartValue('Given'))
    setSurname(getNamePartValue('Surname'))
    setSuffix(getNamePartValue('Suffix'))
    setChangeMessage(name?.attribution?.changeMessage)
  }

  const additionalDataItems = [
    {
      label: t('language.label', 'Language'),
      detail: lang,
    },
  ]

  return (
    <Grid>
      {editable ? (
        <>
          <Cell columns={1}>
            <TextField
              disabled={!editable}
              id="prefix"
              label={t('name.parts.prefix', 'Prefix')}
              value={prefix}
              onChange={(event) => setPrefix(event.target.value)}
            />
          </Cell>
          <Cell columns={4}>
            <TextField
              disabled={!editable}
              id="given"
              label={t('name.parts.given', 'Given')}
              value={given}
              onChange={(event) => setGiven(event.target.value)}
            />
          </Cell>
          <Cell columns={6}>
            <TextField
              disabled={!editable}
              id="surname"
              label={t('name.parts.surname', 'Surname')}
              value={surname}
              onChange={(event) => setSurname(event.target.value)}
            />
          </Cell>
          <Cell columns={1}>
            <TextField
              disabled={!editable}
              id="suffix"
              label={t('name.parts.suffix', 'Suffix')}
              value={suffix}
              onChange={(event) => setSuffix(event.target.value)}
            />
          </Cell>
          <Cell column={12}>
            <TextField
              id="summaryNameChangeReason"
              label={t('reason', 'Reason')}
              value={changeMessage}
              onChange={(event) => setChangeMessage(event.target.value)}
            />
            <Expander setOpen={setChanged} open={changed}>
              <ExpanderContent>
                <Row>
                  <Button emphasis="high" type="submit" id="summaryNameSave" onClick={saveName}>
                    {t('action.save', 'Save')}
                  </Button>
                  <Button emphasis="medium" id="summaryNameCancel" onClick={cancelChanges}>
                    {t('action.cancel', 'Cancel')}
                  </Button>
                </Row>
                <Separator size="xxs" />
              </ExpanderContent>
            </Expander>
          </Cell>
        </>
      ) : (
        <Cell alignY="middle" columns={2}>
          <Paragraph id="summaryFullName" size="sm">
            {fullName}
          </Paragraph>
        </Cell>
      )}
      <PSAttribution
        attribution={name.attribution}
        cellColumns={editable ? 12 : 10}
        additionalDataItems={additionalDataItems}
      />
    </Grid>
  )
}
